import type { ContentAccess } from '../../types/agapellaSubscription';
import React, { useState } from 'react';
import { View, TouchableOpacity, Image, Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import {
  formatRecordingDurationMmSs,
  formatAbsoluteDateRu,
  buildArchiveStatRow,
  type ArchiveCardStats,
} from './ArchiveCard.helpers';
import { AgapellaBadge, badgeGap } from '../subscription/AgapellaBadge';

import { ON_ACCENT_TEXT } from '../../styles/accentText';
import { surfaceFill } from '../../styles/platformBackgrounds';
import { COVER_PLACEHOLDER } from './UpcomingCard';
// Re-export helpers and types for testing.
export {
  formatRecordingDurationMmSs,
  formatAbsoluteDateRu,
  buildArchiveStatRow,
  type ArchiveCardStats,
};

export interface ArchiveCardRoom {
  id: number;
  title: string;
  startDateTimeUtc: string;
  durationMinutes: number;
  coverImageUrl?: string | null;
  masterName: string;
  recordingDurationSec: number;
  isOwn?: boolean;
  /** True when the underlying room belongs to a recurring series (SCRUM-49). */
  isPartOfSeries?: boolean;
  stats: { peakParticipants: number | null; viewCount: number | null; messageCount: number | null };
  /** CORE-1777 — «Входит в Agapēlla»: бейдж на карточке. */
  access?: ContentAccess;
}

interface Props {
  room: ArchiveCardRoom;
  onPress: () => void;
  /**
   * SCRUM-49 — when provided and `room.isOwn`, the «ВЫ БЫЛИ» badge becomes
   * a pencil button that opens the owner action sheet. Non-owners never
   * see it.
   */
  onOpenOwnerMenu?: () => void;
}

export const ArchiveCard: React.FC<Props> = ({ room, onPress, onOpenOwnerMenu }) => {
  // Lazy requires keep jest's node env happy — `ui/Typography` uses
  // StyleSheet.create at module load, and `ShareModal` pulls in
  // expo-clipboard / the ui barrel. Module load executes top-level
  // imports; deferring them to the render body means tests that only
  // import the pure helpers from this file don't blow up on the
  // transitive chain.
  const { Typography } = require('../ui/Typography');
  const { ShareModal } = require('../ShareModal');
  const { buildShareUrl } = require('../../utils/share');
  const { shareOrOpenModal } = require('../../utils/roomShare');
  const { isDark, colors } = useTheme();
  const [showShare, setShowShare] = useState(false);
  const segments = buildArchiveStatRow(room.stats);
  const shareUrl = buildShareUrl('room', room.id);
  const shareAnalytics = { entityType: 'room', entityId: room.id, surface: 'practice_room_card' } as const;

  // CORE-1733 (возврат QA 27.10) — как у UpcomingCard на той же странице: на вебе
  // карточка — поверхность v4 (была rgba(255,255,255,.78) / rgba(40,39,37,.84)),
  // строка статистики — токен темы (#9CA3AF давал 2.3:1), заглушка обложки тёплая.
  const web = Platform.OS === 'web';
  const cardFill = web
    ? surfaceFill(isDark ? 'dark' : 'light')
    : { backgroundColor: colors.surfacePrimary, borderWidth: 1, borderColor: colors.border };
  const titleCol = isDark ? '#F5F3F0' : '#0F1419';
  const mutedCol = web ? colors.textMuted : (isDark ? 'rgba(255,255,255,0.55)' : '#686170');
  const subtleCol = web ? colors.textMuted : (isDark ? 'rgba(255,255,255,0.4)' : '#9CA3AF');
  const placeholderBg = COVER_PLACEHOLDER[isDark ? 'dark' : 'light'];

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      testID={`archive-card-${room.id}`}
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        ...cardFill,
        ...(web ? { boxShadow: colors.shadowSurface } : {}),
      } as any}
    >
      <View style={{ position: 'relative' } as any}>
        {room.coverImageUrl ? (
          <Image source={{ uri: room.coverImageUrl }} style={{ width: '100%', aspectRatio: 16 / 9 } as any} />
        ) : (
          <View style={{ width: '100%', aspectRatio: 16 / 9, backgroundColor: placeholderBg } as any} />
        )}
        <View style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)' } as any} />
        {room.isOwn && (
          onOpenOwnerMenu ? (
            <TouchableOpacity
              onPress={(e) => { e.stopPropagation(); onOpenOwnerMenu(); }}
              accessibilityLabel="Настройки моей Live-сессии"
              testID={`archive-card-${room.id}-owner-menu`}
              style={{ position: 'absolute', top: 8, right: 8, flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: 'rgba(233, 130, 69,0.92)' } as any}
            >
              <Typography variant="captionAccent" style={{ color: ON_ACCENT_TEXT } as any}>ВЫ БЫЛИ</Typography>
              <Ionicons name="pencil" size={11} color={ON_ACCENT_TEXT} />
            </TouchableOpacity>
          ) : (
            <View style={{ position: 'absolute', top: 8, right: 8, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: 'rgba(233, 130, 69,0.92)' } as any}>
              <Typography variant="captionAccent" style={{ color: ON_ACCENT_TEXT } as any}>ВЫ БЫЛИ</Typography>
            </View>
          )
        )}
        <View style={{ position: 'absolute', bottom: 8, right: 8, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: 'rgba(15,20,25,0.78)', flexDirection: 'row', alignItems: 'center', gap: 4 } as any}>
          <Ionicons name="time-outline" size={11} color="#FFF" />
          <Typography variant="captionAccent" style={{ color: '#FFF' } as any}>
            {formatRecordingDurationMmSs(room.recordingDurationSec)}
          </Typography>
        </View>
        <View style={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' } as any} pointerEvents="none">
          <Ionicons name="play-circle" size={48} color="rgba(255,255,255,0.85)" />
        </View>
        <TouchableOpacity
          // CORE-786 — a phone gets the OS sheet, a desktop the popup.
          onPress={(e) => { e.stopPropagation(); void shareOrOpenModal({ url: shareUrl, title: room.title }, shareAnalytics, () => setShowShare(true)); }}
          style={{ position: 'absolute', top: 8, left: 8, width: 28, height: 28, borderRadius: 14, backgroundColor: 'rgba(15,20,25,0.78)', alignItems: 'center', justifyContent: 'center' } as any}
          accessibilityLabel="Поделиться"
          testID={`archive-card-${room.id}-share`}
        >
          <Ionicons name="share-social-outline" size={15} color="#FFF" />
        </TouchableOpacity>
      </View>
      <View style={{ padding: 10, gap: 4 } as any}>
        <AgapellaBadge access={room.access} style={badgeGap} testID={`agapella-badge-room-${room.id}`} />
        <Typography variant="label" numberOfLines={2} style={{ color: titleCol } as any}>
          {room.title}
        </Typography>
        <Typography variant="caption" numberOfLines={1} style={{ color: mutedCol } as any}>
          {formatAbsoluteDateRu(room.startDateTimeUtc)} · {room.masterName}
        </Typography>
        {segments.length > 0 && (
          <Typography variant="caption" numberOfLines={1} style={{ color: subtleCol } as any}>
            {segments.join(' · ')}
          </Typography>
        )}
      </View>
      <ShareModal
        visible={showShare}
        onClose={() => setShowShare(false)}
        url={shareUrl}
        title={room.title}
        analytics={shareAnalytics}
      />
    </TouchableOpacity>
  );
};
